import React, { useEffect, useState } from 'react'
import {
  deleteShopSeoDescription,
  fetchShopSeoDescriptions,
} from 'actions/views/shops'
import useAction from 'utils/useAction'
import {
  getCurrentShopId,
  getShopSeoDescriptions,
  getShopSeoDescriptionsFilters,
  getShopSeoDescriptionsPage,
  getShopSeoDescriptionsPageCount,
  getShopSeoDescriptionsSorter,
  getIsSavingActive,
} from 'domains/shops/selectors'
import { useSelector } from 'react-redux'
import { Button, Modal, Spin } from 'antd'
import { Buttons, StyledTable } from '../Shops.styles'
import { getRegularRowClass, getRowKey } from 'helpers/tables'
import getColumns from './seoDescriptionsColumns'
import {
  openConfirmModal,
  openShopSeoDescriptionModal,
} from 'reducers/modals/actions'
import { FIELD_NAMES } from 'const/forms'
import { PAGE_SIZE } from '../../../const/tables'
import {
  PreviewCard,
  PreviewCardHeader,
  PreviewCardIcon,
  PreviewCardTitle,
  PreviewCardDescription,
  EmptyPreview,
} from './SeoDescriptionsTab.styles'

const SeoDescriptionsTab = () => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const currentShopId = useSelector(getCurrentShopId)
  const seoDescriptions = useSelector(getShopSeoDescriptions)
  const pageCount = useSelector(getShopSeoDescriptionsPageCount)
  const filters = useSelector(getShopSeoDescriptionsFilters)
  const sorter = useSelector(getShopSeoDescriptionsSorter)
  const page = useSelector(getShopSeoDescriptionsPage)
  const isSavingActive = useSelector(getIsSavingActive)

  const fetchShopSeoDescriptionsFunc = useAction(fetchShopSeoDescriptions)
  const openShopSeoDescriptionModalFunc = useAction(openShopSeoDescriptionModal)
  const deleteShopSeoDescriptionFunc = useAction(deleteShopSeoDescription)
  const openConfirmModalFunc = useAction(openConfirmModal)

  const paginationConfig = {
    position: ['bottomCenter'],
    showQuickJumper: true,
    total: pageCount * PAGE_SIZE,
    pageSize: PAGE_SIZE,
    current: page,
    showSizeChanger: false,
  }

  const handleDeleteSeoDescription = (seoDescription) => {
    const callback = () =>
      deleteShopSeoDescriptionFunc(seoDescription[FIELD_NAMES.id])
    const text =
      `Are you sure you want to delete SEO description "${
        seoDescription?.attributes?.title || seoDescription[FIELD_NAMES.id]
      }"? ` + "This action can't be revoked."
    openConfirmModalFunc({ callback, text })
  }

  const onTableChange = (pagination, filters, sorter) => {
    fetchShopSeoDescriptionsFunc({
      page: pagination.current,
      filters,
      sorter,
    })
  }

  const handleEdit = (id) => {
    openShopSeoDescriptionModalFunc({ id })
  }

  const handleCreate = () => {
    openShopSeoDescriptionModalFunc()
  }

  useEffect(() => {
    if (currentShopId) fetchShopSeoDescriptionsFunc()
  }, [currentShopId])

  const renderPreviewContent = () => {
    if (!seoDescriptions || seoDescriptions.length === 0) {
      return (
        <EmptyPreview>
          No SEO descriptions to preview. Add one to see it here.
        </EmptyPreview>
      )
    }

    return seoDescriptions.map((item) => {
      const { title, description, image_url } = item.attributes || {}
      return (
        <PreviewCard key={item.id}>
          <PreviewCardHeader>
            <PreviewCardIcon>{image_url || ''}</PreviewCardIcon>
            <PreviewCardTitle>{title || 'Untitled'}</PreviewCardTitle>
          </PreviewCardHeader>
          <PreviewCardDescription>{description || ''}</PreviewCardDescription>
        </PreviewCard>
      )
    })
  }

  return (
    <Spin spinning={isSavingActive} size="large">
      <Buttons>
        <Button
          onClick={handleCreate}
          disabled={!currentShopId}
          style={{ marginLeft: '12px', marginBottom: '10px' }}
        >
          Add SEO Description
        </Button>
        <Button
          onClick={() => setIsPreviewOpen(true)}
          disabled={!seoDescriptions || seoDescriptions.length === 0}
          style={{ marginBottom: '10px' }}
        >
          Preview
        </Button>
      </Buttons>

      <StyledTable
        onChange={onTableChange}
        dataSource={seoDescriptions}
        columns={getColumns(
          filters,
          sorter,
          handleEdit,
          handleDeleteSeoDescription
        )}
        size="medium"
        pagination={paginationConfig}
        rowKey={getRowKey}
        rowClassName={getRegularRowClass}
        bordered
        sticky
        style={{ margin: '0 12px' }}
      />

      <Modal
        title="SEO Descriptions Preview"
        open={isPreviewOpen}
        onCancel={() => setIsPreviewOpen(false)}
        footer={null}
        width={700}
      >
        {renderPreviewContent()}
      </Modal>
    </Spin>
  )
}

export default SeoDescriptionsTab
